import { compact, flatten } from 'lodash';
import { get } from 'lodash/fp';
import head from 'lodash/fp/head';
import compose from 'lodash/fp/compose';
import { parseContent } from 'client/data/cms/content';
import { EditorialSeo } from 'site-modules/editorial/utils/editorial-seo';
import { getMeta } from 'site-modules/shared/utils/seo-helper';
import { getWebpageSchema } from 'client/seo/schemas/webpage';
import { MAX_WIDTH, MAX_HEIGHT, getFullImageUrl } from 'client/utils/image-helpers';
import { getMonth, createDate } from 'site-modules/shared/utils/date-utils';
import {
  RESEARCH_CATEGORY_EDITORIAL_PATH,
  CAR_NEWS_CATEGORY_EDITORIAL_PATH,
  CAR_NEWS_CATEGORY_NAME,
} from 'site-modules/shared/constants/editorial/research-article';
import { buildAllAuthorsPath, CmsModel } from 'client/data/models/cms';
import { getModelState } from 'client/data/luckdragon/model';

const MAIN_PHOTO_WIDTH = MAX_WIDTH;
const MAIN_PHOTO_HEIGHT = MAX_HEIGHT;

export function getTitleOverride(content) {
  const { id, updated } = content;

  const TITLE_OVERRIDES = {
    '/research/car-leasing/monthly-199-lease-deals': `Best Car Lease Deals This ${getMonth(
      createDate(updated)
    )} Under $199`,
  };

  return TITLE_OVERRIDES[id];
}

/**
 * Seo object build for research article page
 *
 * @param state
 * @param contentPathInState
 * @param categoryName
 * @return {*}
 */
export function getSeoObject(state, contentPathInState, categoryName, subCategoryName) {
  const modelState = getModelState(state);
  const { cms } = state;
  const { content } = cms;

  // Content was not loaded because of preload timeout
  if (!content) {
    return {};
  }

  const articleContent = parseContent(content[contentPathInState]);
  const authorContent = parseContent(
    modelState.get(buildAllAuthorsPath({ contentPath: contentPathInState }), CmsModel)?.author
  );
  const authorName = authorContent.metadata('authorName').value();
  const categoryContent =
    categoryName === CAR_NEWS_CATEGORY_NAME
      ? parseContent(content[CAR_NEWS_CATEGORY_EDITORIAL_PATH])
      : parseContent(content[RESEARCH_CATEGORY_EDITORIAL_PATH]);
  const meta = getMeta(articleContent);
  const { canonical } = meta;

  let title = meta.title;
  const titleOverride = getTitleOverride(articleContent);

  if (titleOverride) {
    title = `${titleOverride} | Edmunds`;
  }

  const seo = new EditorialSeo(categoryContent, articleContent);
  const articleHeroContent = seo.getHeroContentLinks();
  const getImage = compose(
    url => (url ? getFullImageUrl(url, MAIN_PHOTO_WIDTH) : null),
    get('href'),
    head
  );

  const imgUrl = getImage(articleHeroContent) || getImage(articleContent.links());

  return {
    ...meta,
    title,
    image: {
      url: imgUrl,
      width: MAIN_PHOTO_WIDTH,
      height: MAIN_PHOTO_HEIGHT,
    },
    jsonld: flatten(
      compact([
        getWebpageSchema(title),
        seo.getBreadcrumbs({
          categoryName,
          extraItems: [
            {
              href: canonical,
              title,
            },
          ],
          subCategoryName,
        }),
        seo.getPhotoCarouselJsonLd(),
        seo.getPhotoBlocksSchema(),
        seo.getVideoBlocksSchema(),
        seo.getArticlePageSchema(authorName, subCategoryName, categoryName),
        seo.getFAQSchema(canonical),
        seo.getListSchema({ canonical, title }),
        seo.getHowToSchema({ title }),
      ])
    ),
    type: 'article',
    publishedTime: articleContent.published,
    modifiedTime: articleContent.updated,
    articleAuthor: authorName,
  };
}
